import React, { useEffect, useState } from 'react';

/**
 * Exit-intent modal for the Funeral Professional program page.
 *
 * Fires once per session when the cursor leaves the top edge of the
 * viewport (the classic "about to close the tab" signal). Offers the
 * prospect a direct download of the real branded sample PDF so they
 * leave with something concrete even if they don't sign up today.
 *
 * Suppressed on touch devices (no cursor-leave signal), dismissed
 * permanently for the session via sessionStorage, and skipped on the
 * auth / dashboard routes where the visitor has already engaged.
 *
 * No 3rd-party deps; styling piggybacks on the existing glass-card
 * system + a few .fd-exit-* classes in App.css.
 */

const STORAGE_KEY = 'fd-exit-seen';
const EDGE_PX = 10;           // how close to the top edge counts as exit intent
const ARM_DELAY_MS = 6000;    // don't fire until the visitor spent a few seconds

const isTouchDevice = () =>
  typeof window !== 'undefined' &&
  ('ontouchstart' in window ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 0));

const FdExitIntent = ({ sampleHref = '/sample-branded-obituary.pdf' }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (isTouchDevice()) return undefined;
    let fired = false;
    try {
      if (sessionStorage.getItem(STORAGE_KEY)) return undefined;
    } catch (_) { /* sessionStorage can throw in privacy modes */ }

    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, ARM_DELAY_MS);

    const onMove = (e) => {
      if (!armed || fired) return;
      if (e.clientY <= EDGE_PX) {
        fired = true;
        setOpen(true);
        try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch (_) {}
      }
    };
    document.addEventListener('mouseout', onMove);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener('mouseout', onMove);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div className="fd-exit-backdrop" role="presentation" onClick={() => setOpen(false)}>
      <div
        className="fd-exit-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fd-exit-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="fd-exit-close"
          aria-label="Close"
          onClick={() => setOpen(false)}
        >
          ×
        </button>

        <span className="fd-auth-badge">Before you go</span>
        <h2 id="fd-exit-title" className="fd-exit-title">
          Want a real branded sample PDF?
        </h2>
        <p className="fd-exit-sub">
          Grab a finished obituary rendered in our template — the
          firm name and logo would be yours on a real generation.
          Share it with your team before deciding.
        </p>

        <div className="fd-exit-actions">
          <a
            href={sampleHref}
            className="btn btn-primary btn-lg"
            download="ObitAuthor-sample-obituary.pdf"
            onClick={() => setOpen(false)}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
                 stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"
                 strokeLinejoin="round" style={{ marginRight: 6 }} aria-hidden="true">
              <path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M4 21h16" />
            </svg>
            Download sample PDF
          </a>
          <a
            href="/funeral-directors/dashboard?mode=register"
            className="btn btn-outline-primary btn-lg"
          >
            Or start free trial
          </a>
        </div>

        <p className="fd-exit-foot">
          30 obituaries free. No card required. Credits never expire.
        </p>
      </div>
    </div>
  );
};

export default FdExitIntent;
